import type { LscVodHandoffSession } from './types';
import { formatAwayAtHome } from './leagueNav';

export type VodHandoffSessionClassification =
  | 'awaiting_twitch_vod'
  | 'retry_pending'
  | 'retry_exhausted'
  | 'candidate_ready_for_review'
  | 'vod_linked'
  | 'handoff_blocked'
  | 'hidden';

export type VodHandoffSessionBadgeTone = 'success' | 'warning' | 'danger' | 'neutral';

export type VodHandoffSessionBadge = {
  key: string;
  label: string;
  tone: VodHandoffSessionBadgeTone;
};

export type VodHandoffSessionNextAction = {
  label: string;
  href?: string;
  tone: 'neutral' | 'primary';
};

const VERIFIED_LIVE_TRUTH_SOURCE = 'verified_live_truth';
const VOD_NOT_AVAILABLE_YET = 'vod_not_available_yet';
const HANDOFF_STATUS_LINKED = 'vod_candidate_linked';
const HANDOFF_STATUS_ELIGIBLE = 'vod_handoff_eligible';
const HANDOFF_STATUS_LOOKUP_PENDING = 'vod_lookup_pending';
const LIVE_TRUTH_VERIFIED = 'verified_live_game';
const SIDE_CONFIRMATION_HOLD = 'side_confirmation_required';
const DEFAULT_LINK_MAX_ATTEMPTS = 3;

const PANEL_STATUSES = new Set([
  HANDOFF_STATUS_ELIGIBLE,
  HANDOFF_STATUS_LOOKUP_PENDING,
  HANDOFF_STATUS_LINKED,
]);

const PANEL_RECENCY_MS = 7 * 24 * 60 * 60 * 1000;
const PANEL_MAX_SESSIONS = 5;

function optionalString(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized || null;
}

function hasLinkedVod(session: LscVodHandoffSession): boolean {
  return Boolean(optionalString(session.provider_vod_id) && optionalString(session.vod_candidate_id));
}

function isVerifiedLiveHandoffSource(session: LscVodHandoffSession): boolean {
  return optionalString(session.handoff_source) === VERIFIED_LIVE_TRUTH_SOURCE;
}

function isRetryExhausted(session: LscVodHandoffSession): boolean {
  const attempts = Number(session.handoff_attempt_count ?? 0);
  const maxAttempts = Number(session.handoff_link_max_attempts ?? DEFAULT_LINK_MAX_ATTEMPTS);
  if (
    optionalString(session.automation_blocker_code) === VOD_NOT_AVAILABLE_YET &&
    attempts >= maxAttempts &&
    !optionalString(session.provider_vod_id)
  ) {
    return true;
  }
  return optionalString(session.automation_blocker_code) === 'retry_exhausted';
}

export function formatVodHandoffSessionMatchLabel(session: LscVodHandoffSession): string | null {
  const timeline = optionalString(session.timeline_label);
  if (timeline) {
    return timeline;
  }
  const matchup = formatAwayAtHome({
    away_side_label: session.away_team ?? undefined,
    home_side_label: session.home_team ?? undefined,
  });
  if (matchup && matchup !== 'Orientation unavailable') {
    return matchup;
  }
  return optionalString(session.competition_match_id);
}

export function classifyVodHandoffSession(
  session: LscVodHandoffSession
): VodHandoffSessionClassification {
  if (
    session.vod_handoff_eligible === false &&
    optionalString(session.vod_handoff_hold_reason) === SIDE_CONFIRMATION_HOLD
  ) {
    return 'handoff_blocked';
  }

  if (session.vod_handoff_eligible === false && optionalString(session.vod_handoff_hold_reason)) {
    return 'handoff_blocked';
  }

  if (
    optionalString(session.handoff_status) === HANDOFF_STATUS_LINKED ||
    hasLinkedVod(session)
  ) {
    if (optionalString(session.vod_candidate_id)) {
      return 'candidate_ready_for_review';
    }
    return 'vod_linked';
  }

  if (isRetryExhausted(session)) {
    return 'retry_exhausted';
  }

  if (
    optionalString(session.automation_blocker_code) === VOD_NOT_AVAILABLE_YET &&
    !optionalString(session.provider_vod_id) &&
    !optionalString(session.vod_candidate_id) &&
    Number(session.handoff_attempt_count ?? 0) > 0
  ) {
    return 'retry_pending';
  }

  if (
    (optionalString(session.handoff_status) === HANDOFF_STATUS_ELIGIBLE ||
      optionalString(session.handoff_status) === HANDOFF_STATUS_LOOKUP_PENDING) &&
    isVerifiedLiveHandoffSource(session) &&
    session.vod_handoff_eligible === true &&
    !optionalString(session.provider_vod_id) &&
    !optionalString(session.vod_candidate_id) &&
    optionalString(session.live_truth_status) === LIVE_TRUTH_VERIFIED &&
    optionalString(session.stream_ended_at)
  ) {
    if (optionalString(session.handoff_status) === HANDOFF_STATUS_LOOKUP_PENDING) {
      return 'retry_pending';
    }
    return 'awaiting_twitch_vod';
  }

  const blockerCode = optionalString(session.automation_blocker_code);
  if (
    blockerCode &&
    blockerCode !== VOD_NOT_AVAILABLE_YET &&
    blockerCode !== 'retry_exhausted' &&
    optionalString(session.automation_blocker_reason)
  ) {
    return 'handoff_blocked';
  }

  return 'hidden';
}

export function getVodHandoffSessionBadges(
  session: LscVodHandoffSession
): VodHandoffSessionBadge[] {
  const classification = classifyVodHandoffSession(session);
  if (classification === 'handoff_blocked') {
    const hold = optionalString(session.vod_handoff_hold_reason);
    if (hold === SIDE_CONFIRMATION_HOLD) {
      return [{ key: 'side_confirmation', label: 'Side confirmation needed', tone: 'warning' }];
    }
    return [{ key: 'handoff_blocked', label: 'Handoff blocked', tone: 'danger' }];
  }
  if (classification === 'candidate_ready_for_review') {
    return [
      { key: 'verified_live_handoff', label: 'Verified live handoff', tone: 'success' },
      { key: 'candidate_ready', label: 'Archive candidate ready for review', tone: 'success' },
    ];
  }
  if (classification === 'vod_linked') {
    return [{ key: 'vod_linked', label: 'VOD linked', tone: 'success' }];
  }
  if (classification === 'retry_exhausted') {
    return [
      { key: 'verified_live_handoff', label: 'Verified live handoff', tone: 'success' },
      { key: 'retry_exhausted', label: 'VOD lookup needs attention', tone: 'danger' },
    ];
  }
  if (classification === 'retry_pending') {
    return [
      { key: 'verified_live_handoff', label: 'Verified live handoff', tone: 'success' },
      { key: 'retry_pending', label: 'Retrying VOD lookup', tone: 'warning' },
    ];
  }
  if (classification === 'awaiting_twitch_vod') {
    return [
      { key: 'verified_live_handoff', label: 'Verified live handoff', tone: 'success' },
      { key: 'awaiting_twitch_vod', label: 'Waiting for Twitch VOD', tone: 'warning' },
    ];
  }
  return [];
}

export function getVodHandoffSessionPrimaryText(session: LscVodHandoffSession): string | null {
  const classification = classifyVodHandoffSession(session);
  if (classification === 'awaiting_twitch_vod') {
    return 'LSC verified this game live. Waiting for Twitch to expose the archive VOD.';
  }
  if (classification === 'retry_pending') {
    return 'Twitch has not exposed the VOD yet. LSC will keep retrying automatically.';
  }
  if (classification === 'retry_exhausted') {
    return 'Twitch still has not exposed the VOD after automatic retries. Check Needs Attention or contact support.';
  }
  if (classification === 'candidate_ready_for_review') {
    return 'LSC linked the Twitch VOD. The archive candidate is ready for commissioner review.';
  }
  if (classification === 'vod_linked') {
    return 'LSC linked the Twitch VOD automatically. Open review to continue commissioner checks.';
  }
  if (classification === 'handoff_blocked') {
    const blockerReason = optionalString(session.automation_blocker_reason);
    if (blockerReason) {
      return blockerReason;
    }
    return formatHandoffHoldReason(session.vod_handoff_hold_reason);
  }
  return null;
}

function formatHandoffHoldReason(reason?: string | null): string {
  const normalized = optionalString(reason);
  if (!normalized) {
    return 'Verified live handoff is blocked until the hold reason is resolved.';
  }
  if (normalized === SIDE_CONFIRMATION_HOLD) {
    return 'Confirm the broadcaster side before archive handoff can continue.';
  }
  return normalized.replace(/_/g, ' ');
}

export function getVodHandoffSessionNextAction(
  session: LscVodHandoffSession,
  leagueId: string
): VodHandoffSessionNextAction | null {
  const classification = classifyVodHandoffSession(session);
  if (
    (classification === 'vod_linked' || classification === 'candidate_ready_for_review') &&
    optionalString(session.vod_candidate_id)
  ) {
    return {
      label: 'Open review',
      href: `/leagues/${encodeURIComponent(leagueId)}/vods/${encodeURIComponent(
        String(session.vod_candidate_id)
      )}`,
      tone: 'primary',
    };
  }
  if (
    classification === 'awaiting_twitch_vod' ||
    classification === 'retry_pending'
  ) {
    return {
      label: 'No commissioner action needed yet',
      tone: 'neutral',
    };
  }
  if (classification === 'retry_exhausted') {
    return {
      label: 'Review Needs Attention for next steps',
      href: `/leagues/${encodeURIComponent(leagueId)}/needs-attention`,
      tone: 'primary',
    };
  }
  if (classification === 'handoff_blocked') {
    if (optionalString(session.vod_handoff_hold_reason) === SIDE_CONFIRMATION_HOLD) {
      return {
        label: 'Confirm broadcaster side',
        href: `/leagues/${encodeURIComponent(leagueId)}`,
        tone: 'primary',
      };
    }
    return {
      label: 'Resolve hold reason before handoff can continue',
      tone: 'neutral',
    };
  }
  return null;
}

function sessionTouchMs(session: LscVodHandoffSession): number | null {
  const touch = optionalString(session.updated_at) ?? optionalString(session.stream_ended_at);
  if (!touch) {
    return null;
  }
  const parsed = Date.parse(touch);
  return Number.isNaN(parsed) ? null : parsed;
}

export function isPanelRelevantHandoffSession(
  session: LscVodHandoffSession,
  nowMs: number = Date.now()
): boolean {
  if (classifyVodHandoffSession(session) === 'hidden') {
    return false;
  }
  const status = optionalString(session.handoff_status);
  if (status && PANEL_STATUSES.has(status)) {
    return true;
  }
  const touchMs = sessionTouchMs(session);
  if (touchMs == null) {
    return false;
  }
  return nowMs - touchMs <= PANEL_RECENCY_MS;
}

export function selectPanelHandoffSessions(
  sessions: LscVodHandoffSession[],
  nowMs: number = Date.now()
): LscVodHandoffSession[] {
  return sessions
    .filter((session) => isPanelRelevantHandoffSession(session, nowMs))
    .sort((left, right) => (sessionTouchMs(right) ?? 0) - (sessionTouchMs(left) ?? 0))
    .slice(0, PANEL_MAX_SESSIONS);
}

export function shouldShowVerifiedHandoffPanel(
  sessions: LscVodHandoffSession[],
  nowMs: number = Date.now()
): boolean {
  return selectPanelHandoffSessions(sessions, nowMs).length > 0;
}

export function formatVodHandoffSessionLastChecked(
  session: LscVodHandoffSession
): string | null {
  const value = optionalString(session.automation_last_checked_at);
  if (!value) {
    return null;
  }
  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}
